import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Tabs, { visibleTabs } from './components/Tabs';
import AppList from './components/AppList';
import ClickStats from './components/ClickStats';
import StatsFilters from './components/StatsFilters';
import ClickDetailsModal from './components/ClickDetailsModal';
import DeleteAppModal from './components/DeleteAppModal';
import BlockReasons from './components/BlockReasons';
import SettingsPanel from './components/SettingsPanel';
import DocsPanel from './components/DocsPanel';
import TeamPanel from './components/TeamPanel';
import AuditPanel from './components/AuditPanel';
import RepeatsPanel from './components/RepeatsPanel';
import TopBar from './components/TopBar';
import { getPlatform } from './platforms';
import { utcDateKey, utcDateKeyDaysAgo } from './dateTime';

axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('token') || ''}`;

const ALL_COLUMNS = [
  { key: 'clickTime', label: 'Click Time (UTC)', minWidth: 180 },
  { key: 'appId', label: 'App ID', minWidth: 180 },
  { key: 'app_version', label: 'App Version', minWidth: 140 },
  { key: 'deviceID', label: 'Device ID', minWidth: 220 },
  { key: 'androidId', label: 'Android ID', minWidth: 220 },
  { key: 'adId', label: 'Ad ID', minWidth: 220 },
  { key: 'idfa', label: 'IDFA', minWidth: 220 },
  { key: 'idfv', label: 'IDFV', minWidth: 220 },
  { key: 'appVersion', label: 'iOS App Version', minWidth: 150 },
  { key: 'osVersion', label: 'iOS Version', minWidth: 130 },
  { key: 'deviceName', label: 'Device Name', minWidth: 170 },
  { key: 'country', label: 'Country', minWidth: 110 },
  { key: 'naming', label: 'Naming', minWidth: 260 },
  { key: 'onelink', label: 'OneLink', minWidth: 280 },
  { key: 'deeplink', label: 'DeepLink', minWidth: 280 },
  { key: 'referer', label: 'Referer', minWidth: 240 },
  { key: 'installreferrer', label: 'Install Referrer', minWidth: 260 },
  { key: 'finalUrl', label: 'Final URL', minWidth: 320 },
  { key: 'clickLog', label: 'Click Log', minWidth: 220 },
  { key: 'pushToken', label: 'Push Token', minWidth: 220 },
  { key: 'ip', label: 'IP', minWidth: 180 },
  { key: 'userAgent', label: 'User Agent', minWidth: 320 },
];

const DEFAULT_VISIBLE_COLUMNS = [
  'clickTime',
  'appId',
  'deviceID',
  'androidId',
  'adId',
  'naming',
  'onelink',
  'deeplink',
  'referer',
  'finalUrl',
  'clickLog',
  'ip',
];

const buildRange = (start, end) => ({
  startDate: `${start}T00:00:00.000Z`,
  endDate: `${end}T23:59:59.999Z`,
});

export default function MainApp({ onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('android');
  const [apps, setApps] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [count, setCount] = useState(null);
  const [loadingClicks, setLoadingClicks] = useState(false);
  const [selectedClick, setSelectedClick] = useState(null);
  const [showClickModal, setShowClickModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [blockStats, setBlockStats] = useState(null);
  const [reasonMatch, setReasonMatch] = useState('');
  const [statsView, setStatsView] = useState('clicks');
  const [me, setMe] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [filters, setFilters] = useState(() => {
    const today = utcDateKey();
    return { appId: '', startDate: today, endDate: today, deviceID: '', adId: '', urlPart: '', clickLog: '' };
  });

  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [applied, setApplied] = useState(filters);

  useEffect(() => {
    const timer = setTimeout(() => setApplied(filters), 450);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [applied, statusFilter, pageSize, reasonMatch]);

  const isAppsTab = activeTab === 'android' || activeTab === 'ios';
  const platform = getPlatform(activeTab);
  const canWrite = Boolean(me?.superAdmin || me?.permissions?.includes('apps.write'));

  const forceLogout = useCallback(() => {
    onLogout();
    navigate('/login', { replace: true });
  }, [navigate, onLogout]);

  useEffect(() => {
    let cancelled = false;

    axios
      .get('/api/me')
      .then((res) => {
        if (cancelled) return;
        setMe(res.data);
        const allowed = visibleTabs(res.data?.permissions, res.data?.superAdmin);
        setActiveTab((current) =>
          allowed.length && !allowed.some((item) => item.key === current) ? allowed[0].key : current
        );
      })
      .catch((err) => {
        if (err?.response?.status === 401) forceLogout();
      });

    return () => {
      cancelled = true;
    };
  }, [forceLogout]);

  const panelError = useCallback(
    (err) => {
      if (err?.response?.status === 401) {
        forceLogout();
        return;
      }
      console.error('[App] request failed:', err);
      const msg = err?.response?.data?.error || err.message || 'Unknown error';
      window.alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    },
    [forceLogout]
  );

  const fetchApps = useCallback(async () => {
    const cfg = getPlatform(activeTab);
    if (!cfg) return null;

    try {
      const res = await axios.get(cfg.list);
      setApps(res.data);
      return res.data;
    } catch (err) {
      if (err?.response?.status === 401) {
        forceLogout();
        return null;
      }
      console.error('[App] Failed to fetch apps:', err);
      return null;
    }
  }, [activeTab, forceLogout]);

  const confirmDeleteApp = async (app) => {
    if (!platform || !app?.appId) return;

    try {
      await axios.delete(platform.collection, {
        data: { appId: app.appId },
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        forceLogout();
        return;
      }

      console.error('[App] Failed to delete app:', err);
      const msg = err?.response?.data?.error || err.message || 'Невідома помилка';
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }

    setDeleteTarget(null);
    await fetchApps();
  };

  const fetchClicks = useCallback(async () => {
    if (!applied.startDate || !applied.endDate) return;

    setLoadingClicks(true);

    try {
      const range = buildRange(applied.startDate, applied.endDate);
      const trimmed = (value) => (value || '').trim() || undefined;

      const res = await axios.get('/api/clicks/paginated', {
        params: {
          appId: trimmed(applied.appId),
          ...range,
          order: 'desc',
          page,
          pageSize,
          deviceID: trimmed(applied.deviceID),
          adId: trimmed(applied.adId),
          clickLog: trimmed(reasonMatch || applied.clickLog),
          q: trimmed(applied.urlPart),
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
      });

      setClicks(res.data?.items || []);
      setCount(res.data?.total ?? 0);
      setTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      if (err?.response?.status === 401) {
        forceLogout();
        return;
      }
      console.error('[App] Failed to fetch clicks:', err);
    } finally {
      setLoadingClicks(false);
    }
  }, [applied, page, pageSize, statusFilter, reasonMatch, forceLogout]);

  const fetchBlockStats = useCallback(async () => {
    if (!applied.startDate || !applied.endDate) return;

    try {
      const range = buildRange(applied.startDate, applied.endDate);
      const trimmed = (value) => (value || '').trim() || undefined;
      const res = await axios.get('/api/clicks/block-stats', {
        params: {
          appId: trimmed(applied.appId),
          ...range,
          deviceID: trimmed(applied.deviceID),
          adId: trimmed(applied.adId),
          clickLog: trimmed(reasonMatch || applied.clickLog),
          q: trimmed(applied.urlPart),
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
      });
      setBlockStats(res.data || null);
    } catch (err) {
      console.error('[App] Failed to fetch block stats:', err);
    }
  }, [applied, statusFilter, reasonMatch]);

  const exportCsv = () => {
    const rows = clicks || [];
    if (!rows.length) return;

    const esc = (v) => {
      const raw = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      const s = raw.replaceAll('"', '""');
      return `"${s}"`;
    };

    const header = [
      'clickTime',
      'appId',
      'app_version',
      'deviceID',
      'androidId',
      'adId',
      'idfa',
      'idfv',
      'appVersion',
      'osVersion',
      'deviceName',
      'country',
      'pushToken',
      'clickLog',
      'referer',
      'installreferrer',
      'onelink',
      'deeplink',
      'finalUrl',
      'naming',
      'ip',
      'userAgent',
    ];

    const body = rows.map((r) =>
      [
        esc(r.clickTime),
        esc(r.appId),
        esc(r.app_version),
        esc(r.deviceID),
        esc(r.androidId),
        esc(r.adId),
        esc(r.idfa),
        esc(r.idfv),
        esc(r.appVersion),
        esc(r.osVersion),
        esc(r.deviceName),
        esc(r.country),
        esc(r.pushToken),
        esc(r.clickLog),
        esc(r.referer),
        esc(r.installreferrer),
        esc(r.onelink),
        esc(r.deeplink),
        esc(r.finalUrl),
        esc(r.naming),
        esc(r.ip),
        esc(r.userAgent),
      ].join(',')
    );

    const csv = [header.join(','), ...body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${applied.appId || 'app'}_${applied.startDate}_${applied.endDate}_clicks.csv`;
    a.click();

    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!isAppsTab) return;
    setApps([]);
    fetchApps();
  }, [isAppsTab, fetchApps]);

  useEffect(() => {
    if (!isAppsTab) return undefined;

    const intervalId = setInterval(() => {
      fetchApps();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [isAppsTab, fetchApps]);

  useEffect(() => {
    if (activeTab !== 'statistics') return;
    fetchClicks();
  }, [activeTab, fetchClicks]);

  useEffect(() => {
    if (activeTab !== 'statistics') return;
    fetchBlockStats();
  }, [activeTab, fetchBlockStats]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((item) => item !== key);
      }

      return [...prev, key];
    });
  };

  const resetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  return (
    <>
      <TopBar me={me} onLogout={forceLogout} />

      <div className="container app-dashboard-page">
      <div className="app-dashboard-shell">
        <div className="nav-row">
        <Tabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          permissions={me?.permissions}
          superAdmin={me?.superAdmin}
        />

          {isAppsTab ? (
            <button
              type="button"
              className="fbtn is-primary add-app"
              onClick={() => navigate(`/${activeTab}/apps/new`)}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
                <path d="M12 5.5v13M5.5 12h13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
              {platform?.label} app
            </button>
          ) : null}
        </div>

        {isAppsTab && (
          <AppList
            apps={apps}
            platformLabel={platform?.label}
            onEdit={(app) => navigate(`/${activeTab}/apps/${encodeURIComponent(app.appId)}/edit`)}
            onViewStats={(app) => {
              setFilters((prev) => ({
                ...prev,
                appId: app.appId,
                startDate: utcDateKeyDaysAgo(6),
                endDate: utcDateKey(),
                deviceID: '',
                adId: '',
                urlPart: '',
                clickLog: '',
              }));
              setStatusFilter('all');
              setReasonMatch('');
              setActiveTab('statistics');
            }}
            onDelete={setDeleteTarget}
            canWrite={canWrite}
          />
        )}

        {activeTab === 'statistics' && (
          <div className="stats-subtabs">
            <button
              type="button"
              className={`subtab${statsView === 'clicks' ? ' is-active' : ''}`}
              onClick={() => setStatsView('clicks')}
            >
              Кліки
            </button>
            <button
              type="button"
              className={`subtab${statsView === 'repeats' ? ' is-active' : ''}`}
              onClick={() => setStatsView('repeats')}
            >
              Повторні кліки
            </button>
          </div>
        )}

        {activeTab === 'statistics' && statsView === 'repeats' && (
          <RepeatsPanel onError={panelError} />
        )}

        {activeTab === 'statistics' && statsView === 'clicks' && (
          <>
            <StatsFilters
              filters={filters}
              setFilters={setFilters}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              onSearch={fetchClicks}
              count={count}
              filteredCount={clicks.length}
              blockedCount={blockStats?.blocked ?? 0}
              onExport={exportCsv}
              loading={loadingClicks}
            />

            <BlockReasons
              stats={blockStats}
              loading={loadingClicks}
              activeMatch={reasonMatch}
              onSelect={setReasonMatch}
            />

            <ClickStats
              clicks={clicks}
              count={count}
              loading={loadingClicks}
              columns={ALL_COLUMNS}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumn}
              onResetColumns={resetColumns}
              onRowClick={(click) => {
                setSelectedClick(click);
                setShowClickModal(true);
              }}
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              total={count ?? 0}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}

        {activeTab === 'settings' && <SettingsPanel onUnauthorized={forceLogout} />}

        {activeTab === 'team' && <TeamPanel onError={panelError} currentUser={me} />}

        {activeTab === 'audit' && <AuditPanel onError={panelError} />}

        {activeTab === 'docs' && <DocsPanel />}
      </div>

      <ClickDetailsModal
        show={showClickModal}
        onHide={() => setShowClickModal(false)}
        click={selectedClick}
      />

      <DeleteAppModal
        show={Boolean(deleteTarget)}
        app={deleteTarget}
        platform={activeTab}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteApp}
      />
      </div>
    </>
  );
}
